import {
  Button,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { Play, X } from 'lucide-react'
import React, { useState } from 'react'

import type { MusicOnHoldApi } from '../api/musicOnHoldApi.types'
import { useGetAudioTrack } from '../queries/musicOnHoldQueries'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'

export interface TrackPlayButtonProps {
  api: MusicOnHoldApi
  pbxId: number
  /** music-service track uuid (the `id`/`uuid` on AudioTrackSummary). */
  trackUuid?: string
  t?: MusicOnHoldTranslateFn
}

/**
 * Per-track preview control. The track list endpoint doesn't include a
 * playable URL, so we lazily fetch the single track (which carries a presigned
 * file_url from music-service) only once the user clicks play, then swap the
 * button for a native audio player.
 */
export const TrackPlayButton: React.FC<TrackPlayButtonProps> = ({
  api,
  pbxId,
  trackUuid,
  t,
}) => {
  const [requested, setRequested] = useState(false)

  // Gated on `requested` so nothing loads until the user opts to play.
  const { data, isLoading, error } = useGetAudioTrack(
    api,
    requested ? pbxId : undefined,
    requested ? trackUuid : undefined,
  )

  if (!trackUuid) {
    return null
  }

  if (!requested) {
    return (
      <Button
        size="xs"
        variant="ghost"
        borderRadius="lg"
        onClick={() => setRequested(true)}
        aria-label={text(t, 'musicOnHold.trackPlay.play', 'Play')}
      >
        <Icon as={Play} />
        {text(t, 'musicOnHold.trackPlay.play', 'Play')}
      </Button>
    )
  }

  if (isLoading) {
    return (
      <HStack gap={2} color="fg.muted">
        <Spinner size="xs" />
        <Text fontSize="xs">
          {text(t, 'musicOnHold.trackPlay.loading', 'Loading…')}
        </Text>
      </HStack>
    )
  }

  if (error || !data?.file_url) {
    // Reset to the Play button so the user can retry (e.g. expired URL).
    return (
      <Button
        size="xs"
        variant="ghost"
        borderRadius="lg"
        color="fg.error"
        onClick={() => setRequested(false)}
      >
        {text(t, 'musicOnHold.trackPlay.error', 'Preview unavailable')}
      </Button>
    )
  }

  return (
    <HStack gap={1} justify="flex-end">
      <audio
        controls
        autoPlay
        preload="none"
        onClick={(e) => e.stopPropagation()}
        style={{ height: 32, minWidth: 200, maxWidth: 260, borderRadius: 999 }}
      >
        <source src={data.file_url} />
      </audio>
      <IconButton
        size="xs"
        variant="ghost"
        borderRadius="lg"
        onClick={() => setRequested(false)}
        aria-label={text(t, 'musicOnHold.trackPlay.close', 'Close preview')}
      >
        <Icon as={X} />
      </IconButton>
    </HStack>
  )
}
